import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

const GENERATE_CALLABLE_TIMEOUT_MS = 330_000;

export type GenerateDividendShareholderResolutionInput = {
  legalEntityId: string;
  companyRegister?: string;
  fiscalYear: string;
  dividendAmountEur: number;
  resolutionDate?: string;
  distributionDate: string;
  managingDirector: string;
  shareholdersBlock: string;
  fileName?: string;
};

export type GenerateDividendShareholderResolutionResponse = {
  success: boolean;
  fileName: string;
  documentId: string;
  storagePath: string;
  downloadUrl: string;
};

export function generateDividendShareholderResolutionErrorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message: string }).message);
    if (message && !message.startsWith('Firebase')) {
      return message;
    }
  }
  if (err && typeof err === 'object' && 'code' in err) {
    const code = String((err as { code: string }).code);
    if (code === 'functions/invalid-argument') {
      return 'Check all required fields and try again.';
    }
    if (code === 'functions/not-found') {
      return 'Legal entity not found.';
    }
    if (code === 'functions/permission-denied') {
      return 'You do not have access to this legal entity.';
    }
    if (code === 'functions/failed-precondition') {
      return 'Document template is not configured yet. Contact support.';
    }
    if (code === 'functions/unauthenticated') {
      return 'Sign in to generate documents.';
    }
    if (code === 'functions/unavailable') {
      return 'Document service is temporarily unavailable. Try again.';
    }
    if (code === 'functions/internal' || code === 'functions/deadline-exceeded') {
      return 'Document generation failed. Try again in a moment.';
    }
  }
  return err instanceof Error ? err.message : 'Document generation failed.';
}

export async function generateDividendShareholderResolution(
  input: GenerateDividendShareholderResolutionInput
): Promise<GenerateDividendShareholderResolutionResponse> {
  const fn = httpsCallable<
    GenerateDividendShareholderResolutionInput,
    GenerateDividendShareholderResolutionResponse
  >(functions, 'generateDividendShareholderResolution', {
    timeout: GENERATE_CALLABLE_TIMEOUT_MS,
  });
  const result = await fn(input);
  return result.data;
}
